/**
 * 비즈니스 동업 정밀 분석 엔진
 * 두 사람의 사주 교차 분석 · 2026 세운 시너지 반영
 */
import type { SajuResult } from './sajuCalculations';

// ── 기본 상수 ───────────────────────────────────────────────────
const EL = [0,0,1,1,2,2,3,3,4,4]; // 천간→오행
const GEN=[1,2,3,4,0],CTRL=[2,3,4,0,1],GENBY=[4,0,1,2,3],CTRLBY=[3,4,0,1,2];
const BKR=['자','축','인','묘','진','사','오','미','신','유','술','해'];
const BCN=['子','丑','寅','卯','辰','巳','午','未','申','酉','戌','亥'];
const SKR=['갑','을','병','정','무','기','경','신','임','계'];
const SCN=['甲','乙','丙','丁','戊','己','庚','辛','壬','癸'];

function sip(dayIdx:number,tIdx:number):string{
  const d=EL[dayIdx],t=EL[tIdx],dy=dayIdx%2,ty=tIdx%2;
  if(t===d) return dy===ty?'비견':'겁재';
  if(t===GEN[d]) return dy===ty?'식신':'상관';
  if(t===CTRL[d]) return dy===ty?'편재':'정재';
  if(t===CTRLBY[d]) return dy===ty?'편관':'정관';
  if(t===GENBY[d]) return dy===ty?'편인':'정인';
  return '비견';
}

function rels(a:number,b:number):string[]{
  const r:string[]=[];
  if((a+6)%12===b||(b+6)%12===a) r.push('충');
  const yh=[[0,1],[2,11],[3,10],[4,9],[5,8],[6,7]];
  if(yh.some(p=>(p[0]===a&&p[1]===b)||(p[1]===a&&p[0]===b))) r.push('육합');
  const sh=[[2,6,10],[11,3,7],[5,9,1],[8,0,4]];
  if(sh.some(g=>g.includes(a)&&g.includes(b)&&a!==b)) r.push('삼합');
  if([[2,3,4],[5,6,7],[8,9,10],[11,0,1]].some(g=>g.includes(a)&&g.includes(b)&&a!==b)) r.push('방합');
  if([[2,5],[5,8],[8,2],[1,10],[10,7],[7,1],[0,3],[3,0]].some(p=>p[0]===a&&p[1]===b)) r.push('형');
  if(a===b&&[4,6,9,11].includes(a)) r.push('자형');
  if([[0,9],[9,0],[6,3],[3,6],[4,1],[1,4],[7,10],[10,7],[2,11],[11,2],[5,8],[8,5]].some(p=>p[0]===a&&p[1]===b)) r.push('파');
  if([[0,7],[7,0],[1,6],[6,1],[2,5],[5,2],[3,4],[4,3],[8,11],[11,8],[9,10],[10,9]].some(p=>p[0]===a&&p[1]===b)) r.push('해');
  return r.length?r:['없음'];
}

function relLabel(rList:string[]):string{
  if(rList.includes('삼합')) return '삼합(三合)';
  if(rList.includes('육합')) return '육합(六合)';
  if(rList.includes('방합')) return '방합(方合)';
  if(rList.includes('충')) return '충(冲)';
  if(rList.includes('형')) return '형(刑)';
  if(rList.includes('파')) return '파(破)';
  if(rList.includes('해')) return '해(害)';
  return '중성';
}

// 일간 물상
const META:{[k:number]:{ko:string;obj:string;role:string}}={
  0:{ko:'갑목(甲木)',obj:'높은 소나무',role:'비전 제시 · 전략 리더'},
  1:{ko:'을목(乙木)',obj:'유연한 덩굴',role:'관계 관리 · HR · 유연한 실무'},
  2:{ko:'병화(丙火)',obj:'하늘의 태양',role:'대외 활동 · 마케팅 · 얼굴마담'},
  3:{ko:'정화(丁火)',obj:'어둠을 밝히는 촛불',role:'정밀 기획 · 재무 · 핵심 업무'},
  4:{ko:'무토(戊土)',obj:'거대한 산',role:'총괄 관리 · 안정 유지 · 조직화'},
  5:{ko:'기토(己土)',obj:'비옥한 논밭',role:'고객 대응 · 내부 관리 · 세심한 실무'},
  6:{ko:'경금(庚金)',obj:'날카로운 도끼',role:'강력한 영업 · 결단 · 구조화'},
  7:{ko:'신금(辛金)',obj:'날카로운 보석',role:'품질 관리 · 재무 분석 · 정밀 기획'},
  8:{ko:'임수(壬水)',obj:'넓은 바다',role:'전략 기획 · 대외 관계 · 투자 판단'},
  9:{ko:'계수(癸水)',obj:'생명의 빗물',role:'연구개발 · 내부 설계 · 데이터 분석'},
};

// ── 십신 관계 → 동업 메타포 ─────────────────────────────────────
function sipToBiz(sipAB:string,sipBA:string,metaA:{obj:string},metaB:{obj:string}):string{
  if(sipAB==='편재'||sipAB==='정재')
    return `${metaA.obj}(대표님)이 ${metaB.obj}(파트너)을 다루어 가치를 만드는 구조입니다. 명리학적으로 대표님이 파트너로부터 재물(${sipAB})을 얻는 관계이며, 대표님의 에너지가 파트너를 '원재료'로 활용해 결과물을 만들어냅니다.`;
  if(sipBA==='편재'||sipBA==='정재')
    return `${metaB.obj}(파트너)이 ${metaA.obj}(대표님)을 다루어 가치를 만드는 구조입니다. 파트너가 대표님으로부터 재물(${sipBA})을 얻는 관계로, 파트너의 추진력이 대표님의 능력을 원재료 삼아 결과물을 만들어냅니다.`;
  if(sipAB==='편관'||sipAB==='정관')
    return `파트너(${metaB.obj})가 대표님(${metaA.obj})에게 ${sipAB} 역할을 합니다. 파트너가 조직과 규율을 잡아주는 구조입니다. 파트너가 방향을 제시하고 대표님이 실행하면 최상의 궁합이 됩니다.`;
  if(sipBA==='편관'||sipBA==='정관')
    return `대표님(${metaA.obj})이 파트너(${metaB.obj})에게 ${sipBA} 역할을 합니다. 대표님이 파트너를 이끌고 통제하는 구조로, 대표님이 CEO·최종 결정권자 포지션을 맡아야 합니다.`;
  if(sipAB==='편인'||sipAB==='정인')
    return `파트너(${metaB.obj})가 대표님(${metaA.obj})에게 ${sipAB} 역할을 합니다. 파트너가 대표님을 지원하고 육성하는 인성의 관계입니다. 파트너는 든든한 후원자 역할을 하게 됩니다.`;
  if(sipBA==='편인'||sipBA==='정인')
    return `대표님(${metaA.obj})이 파트너(${metaB.obj})에게 ${sipBA} 역할을 합니다. 대표님이 파트너를 지원하는 구조로, 대표님의 경험과 통찰이 파트너를 성장시킵니다.`;
  if(sipAB==='식신'||sipAB==='상관')
    return `대표님(${metaA.obj})의 에너지가 파트너(${metaB.obj})에게 흘러들어가는 구조(${sipAB})입니다. 대표님의 창의력과 아이디어가 파트너를 통해 세상에 표현됩니다.`;
  // 비견/겁재
  return `대표님과 파트너가 동일한 오행 에너지를 가진 ${sipAB} 관계입니다. 강점이 겹치는 만큼 경쟁과 협력이 동시에 존재합니다. 철저한 역할 분담이 이 동업의 성패를 가릅니다.`;
}

// 일지 관계 → 동업 의미
function branchToBiz(rList:string[],aCN:string,bCN:string,aKR:string,bKR:string):string{
  const combo=`${aCN}${bCN}(${aKR}${bKR})`;
  if(rList.includes('삼합')||rList.includes('육합'))
    return `두 분의 일지(日支)가 **${combo} 합(合)**을 이룹니다. 두 사람이 만나면 새로운 오행 에너지가 탄생하는, 명리학에서 가장 강력한 결속 관계입니다. 함께 있을수록 시너지가 커지고, 자연스럽게 재물이 만들어지는 구조입니다.`;
  if(rList.includes('방합'))
    return `두 분의 일지가 **${combo} 방합(方合)**을 이룹니다. 같은 방향을 향해 에너지가 결집되는 강력한 연대감입니다. 같은 업종·분야에서 시작하면 최대 시너지가 납니다.`;
  if(rList.includes('충'))
    return `두 분의 일지가 **${combo} 충(冲)**을 이룹니다. 충은 강렬한 에너지 충돌입니다. 서로의 핵심 가치관이나 업무 방식이 정반대일 수 있어 초반 마찰이 예상됩니다. 단, 충이 잘 관리되면 서로의 단점을 날카롭게 보완하는 최강의 파트너가 됩니다.`;
  if(rList.includes('형'))
    return `두 분의 일지가 **${combo} 형(刑)**을 이룹니다. 형은 형태를 깎고 다듬는 에너지입니다. 서로에게 상처를 주며 성장하는 관계로, 장기 동업 시 서로를 존중하는 명확한 선이 필요합니다.`;
  if(rList.includes('파'))
    return `두 분의 일지가 **${combo} 파(破)**를 이룹니다. 파는 서서히 균열이 가는 기운입니다. 겉으로는 멀쩡해 보이지만 내부에서 이해관계가 어긋나기 쉬운 구조입니다. 금전·지분 관계를 초기에 가장 명확하게 문서화해 두셔야 합니다.`;
  if(rList.includes('해'))
    return `두 분의 일지가 **${combo} 해(害)**를 이룹니다. 표면적 협력 아래 서로의 이해관계가 충돌하기 쉬운 구조입니다. 신뢰보다 계약으로 관계를 지탱해야 합니다.`;
  return `두 분의 일지가 직접적인 합충 관계 없이 중성(中性)을 이룹니다. 드라마틱한 케미는 없지만 안정적으로 협력할 수 있는 무난한 조합입니다.`;
}

// 2026 세운 분석
const YS=2,YB=6; // 병(丙), 오(午)
function yearTimingText(person:SajuResult,who:string):string{
  const ds=person.dayPillar.stemIdx;
  const db=person.dayPillar.branchIdx;
  const sipS=sip(ds,YS);
  const brRels=rels(db,YB);
  const relLbl=relLabel(brRels);
  const meta=META[ds];
  
  // 천간합 체크 (일간 vs 병(2))
  const isComb=(Math.abs(ds-YS)===5)||(ds===2&&YS===7)||(ds===7&&YS===2)||
    (ds+YS===9&&[2,3,4].includes(Math.min(ds,YS)))||
    ((ds-YS+10)%10===5)||((YS-ds+10)%10===5);
  const combText=isComb?` 특히 일간이 2026년 세운 병화(丙火)와 천간합(天干合)을 이루어, 외부의 큰 계약이나 파트너십이 성사되기 매우 좋은 해입니다.`:'';
  
  let text=`${who}에게 2026년은 일간 ${meta.ko}에게 ${sipS}이 되는 해입니다. `;
  if(sipS==='겁재'||sipS==='비견') text+=`경쟁 에너지가 넘치고 주도권 다툼이 생기기 쉬운 해입니다. 하지만 이 강렬한 에너지를 동업에 쏟아붓는 방식으로 전환하면 오히려 큰 추진력이 됩니다.`;
  else if(sipS==='편재'||sipS==='정재') text+=`재물운이 강하게 작용하는 해입니다. 들어오는 현금 흐름을 반드시 안전 자산으로 묶어두는 원칙이 필요합니다.`;
  else if(sipS==='편관'||sipS==='정관') text+=`강한 외부 압박과 함께 공식적인 인정·승진·계약이 따라오는 해입니다. 직함이나 공식 역할을 명확히 정립할 적기입니다.`;
  else if(sipS==='편인'||sipS==='정인') text+=`귀인과 조력자가 나타나는 해입니다. 배움과 지식 습득, 인맥 확장에 투자하면 큰 결실을 맺습니다.`;
  else if(sipS==='식신') text+=`재능과 창의력이 폭발하는 해입니다. 숨겨온 아이디어를 마음껏 펼치고 표현하세요.`;
  else text+=`새로운 시도와 변화가 많은 다이내믹한 해입니다. 에너지 관리에 집중하세요.`;
  
  if(!brRels.includes('없음')&&relLbl!=='중성') {
    text+=` 또한 일지와 세운지 午가 ${relLbl}을 이루어 ${brRels.includes('충')?'현재 환경에 큰 변화가 예고됩니다.':brRels.includes('삼합')||brRels.includes('육합')?'긍정적인 기운이 더욱 강화됩니다.':'보이지 않는 변수에 주의가 필요합니다.'}`;
  }
  text+=combText;
  return text;
}

// 오행 보완성 계산 (0~100)
function ohaengComplement(a:SajuResult,b:SajuResult):number{
  // 두 일간의 오행이 상생 관계면 높은 점수
  const da=EL[a.dayPillar.stemIdx],db=EL[b.dayPillar.stemIdx];
  if(GEN[da]===db||GEN[db]===da) return 85; // 상생
  if(CTRL[da]===db||CTRL[db]===da) return 60; // 상극 (활용 가능)
  if(da===db) return 50; // 동일
  return 70; // 나머지
}

// 레이더 점수 계산
export interface DongupRadar{money:number;role:number;comm:number;values:number;stability:number}

function calcRadar(a:SajuResult,b:SajuResult):DongupRadar{
  const sipAB=sip(a.dayPillar.stemIdx,b.dayPillar.stemIdx);
  const sipBA=sip(b.dayPillar.stemIdx,a.dayPillar.stemIdx);
  const dayRelList=rels(a.dayPillar.branchIdx,b.dayPillar.branchIdx);
  const yrRelList=rels(a.yearPillar.branchIdx,b.yearPillar.branchIdx);
  const mthRelList=rels(a.monthPillar.branchIdx,b.monthPillar.branchIdx);
  
  // 재물 시너지
  let money=55;
  if(['편재','정재'].includes(sipAB)||['편재','정재'].includes(sipBA)) money+=30;
  if(dayRelList.includes('삼합')||dayRelList.includes('육합')) money+=15;
  if(dayRelList.includes('충')) money-=10;
  
  // 역할 보완성
  let role=60;
  if(['편관','정관','편재','정재'].includes(sipAB)||['편관','정관','편재','정재'].includes(sipBA)) role+=20;
  if(EL[a.dayPillar.stemIdx]!==EL[b.dayPillar.stemIdx]) role+=10;
  
  // 소통 에너지
  let comm=60;
  if(['편인','정인','식신'].includes(sipAB)||['편인','정인','식신'].includes(sipBA)) comm+=20;
  if(mthRelList.includes('삼합')||mthRelList.includes('육합')) comm+=15;
  if(mthRelList.includes('충')) comm-=15;
  
  // 가치관 일치도
  let values=55;
  if(yrRelList.includes('삼합')||yrRelList.includes('육합')) values+=25;
  if(yrRelList.includes('충')) values-=20;
  if(yrRelList.includes('방합')) values+=15;
  
  // 장기 안정성
  let stability=60;
  if(dayRelList.includes('삼합')||dayRelList.includes('육합')||dayRelList.includes('방합')) stability+=20;
  if(dayRelList.includes('충')) stability-=15;
  if(dayRelList.includes('파')||dayRelList.includes('해')) stability-=10;
  if(['비견','겁재'].includes(sipAB)) stability-=8;
  
  const cap=(v:number)=>Math.max(25,Math.min(98,v));
  return {money:cap(money),role:cap(role),comm:cap(comm),values:cap(values),stability:cap(stability)};
}

// ── 메인 분석 함수 ───────────────────────────────────────────────
export interface DongupAnalysis {
  personA:{pillarStr:string;label:string;obj:string;role:string};
  personB:{pillarStr:string;label:string;obj:string;role:string};
  score:number;scoreLabel:string;
  sipAB:string;sipBA:string;
  dayBranchRels:string[];dayBranchLabel:string;
  yearBranchRels:string[];yearBranchLabel:string;
  overallTitle:string;overallMeta:string;overallText:string;
  branchBizText:string;
  roleA:{title:string;desc:string};
  roleB:{title:string;desc:string};
  timing2026:{a:string;b:string;combined:string};
  risks:Array<{title:string;desc:string}>;
  radar:DongupRadar;
}

export function analyzeDongup(a:SajuResult,b:SajuResult,nameA:string,nameB:string):DongupAnalysis{
  const da=a.dayPillar.stemIdx,db=b.dayPillar.stemIdx;
  const ba=a.dayPillar.branchIdx,bb=b.dayPillar.branchIdx;
  const ya=a.yearPillar.branchIdx,yb=b.yearPillar.branchIdx;
  
  const sipAB=sip(da,db);
  const sipBA=sip(db,da);
  const dayRelList=rels(ba,bb);
  const yrRelList=rels(ya,yb);
  const metaA=META[da],metaB=META[db];
  
  const radar=calcRadar(a,b);
  const score=Math.round((radar.money+radar.role+radar.comm+radar.values+radar.stability)/5);
  const scoreLabel=score>=85?'S급 시너지':score>=75?'강력 추천(A급)':score>=65?'보완적 조합(B급)':score>=55?'신중 검토(C급)':'고리스크(D급)';
  
  const pillarA=`${a.yearPillar.stem}${a.yearPillar.branch} ${a.monthPillar.stem}${a.monthPillar.branch} ${a.dayPillar.stem}${a.dayPillar.branch}${a.hourPillar?` ${a.hourPillar.stem}${a.hourPillar.branch}`:''}`;
  const pillarB=`${b.yearPillar.stem}${b.yearPillar.branch} ${b.monthPillar.stem}${b.monthPillar.branch} ${b.dayPillar.stem}${b.dayPillar.branch}${b.hourPillar?` ${b.hourPillar.stem}${b.hourPillar.branch}`:''}`;
  
  // 총론 메타포 (일간 오행 관계 기반)
  const da_el=EL[da],db_el=EL[db];
  const elNames=['나무','불','흙','쇠','물'];
  const aEl=elNames[da_el],bEl=elNames[db_el];
  let overallMeta='';
  if(CTRL[da_el]===db_el) overallMeta=`${metaA.obj}(${nameA||'대표님'})이 ${metaB.obj}(${nameB||'파트너'})을 다루어 결과물을 만드는 형국입니다.`;
  else if(CTRL[db_el]===da_el) overallMeta=`${metaB.obj}(${nameB||'파트너'})이 ${metaA.obj}(${nameA||'대표님'})에게 강력한 방향성을 제시하는 형국입니다.`;
  else if(GEN[da_el]===db_el) overallMeta=`${metaA.obj}(${nameA||'대표님'})의 에너지가 ${metaB.obj}(${nameB||'파트너'})를 키우고 성장시키는 형국입니다.`;
  else if(GEN[db_el]===da_el) overallMeta=`${metaB.obj}(${nameB||'파트너'})의 에너지가 ${metaA.obj}(${nameA||'대표님'})를 뒷받침하는 든든한 형국입니다.`;
  else overallMeta=`${aEl}(${nameA||'대표님'})과 ${bEl}(${nameB||'파트너'})이 만나 서로를 자극하며 성장하는 형국입니다.`;
  
  const overallText=sipToBiz(sipAB,sipBA,metaA,metaB);
  const branchBizText=branchToBiz(dayRelList,BCN[ba],BCN[bb],BKR[ba],BKR[bb]);
  
  // 역할 분담
  // 재성 관계 가진 쪽이 "기획/관리", 반대편이 "영업/대외"
  const aIsBoss=sipBA==='편관'||sipBA==='정관'||sipAB==='편재'||sipAB==='정재';
  const bIsBoss=sipAB==='편관'||sipAB==='정관'||sipBA==='편재'||sipBA==='정재';
  const roleA={
    title: aIsBoss?'[CEO / 최종 결정권자 / 기획 총괄]':`[${metaA.role}]`,
    desc: aIsBoss
      ?`${metaA.ko} 일간은 명리학적으로 이 동업에서 실질적인 결정권을 쥐어야 합니다. 사업 방향성, 핵심 기획, 중요한 계약서에 도장 찍는 역할이 ${nameA||'대표님'} 몫입니다. 나이나 경험과 무관하게, 이 구조에서는 ${nameA||'대표님'}이 칼자루를 쥐어야만 결과물이 나옵니다.`
      :`${metaA.ko}의 성질을 살려 ${metaA.role} 역할을 담당하세요. ${nameA||'대표님'}의 강점이 극대화되는 포지션입니다.`,
  };
  const roleB={
    title: bIsBoss?'[CEO / 최종 결정권자 / 기획 총괄]':`[${metaB.role}]`,
    desc: bIsBoss
      ?`${metaB.ko} 일간은 이 동업에서 실질적인 결정권과 방향성을 담당해야 합니다. 큰 그림을 그리고 외부와의 협상을 이끄는 역할입니다.`
      :`${metaB.ko}의 성질을 살려 ${metaB.role} 역할을 담당하세요. ${nameB||'파트너'}의 강점이 극대화되는 포지션입니다.`,
  };
  
  // 2026 타이밍
  const timingA=yearTimingText(a,nameA||'대표님');
  const timingB=yearTimingText(b,nameB||'파트너');
  let combined='';
  if(score>=75) combined='2026년은 두 분이 힘을 합쳤을 때 최대 시너지가 터지는 해입니다. 병오년의 강렬한 화기가 두 분의 에너지를 모두 끌어올리며, 적극적인 사업 확장의 원년이 될 수 있습니다. 단, 빠른 성장에 따른 과열을 방지하기 위해 재무 관리는 보수적으로 접근하세요.';
  else if(score>=60) combined='2026년은 동업의 첫 발을 내딛기에 적당한 해입니다. 너무 급하게 규모를 키우기보다, 시스템과 역할 분담을 다지며 기반을 쌓는 데 집중하세요.';
  else combined='2026년은 두 분 각자에게 강한 에너지가 부딪히는 해입니다. 동업을 시작한다면 반드시 감정 관리와 역할 경계를 명확히 해야 갈등을 예방할 수 있습니다.';
  
  // 리스크
  const risks:Array<{title:string;desc:string}>=[];
  if(['비견','겁재'].includes(sipAB)||['비견','겁재'].includes(sipBA)){
    risks.push({title:'"같은 오행, 칼자루 싸움을 조심하라"',desc:'두 분의 일간이 같거나 비슷한 에너지를 가지고 있어 자연스럽게 주도권 경쟁이 생깁니다. 의사결정 구조를 초기에 명확히 정립하는 것이 이 동업의 핵심입니다.'});
  }
  if(dayRelList.includes('충')){
    risks.push({title:'"일지 충, 스타일 차이를 다루어라"',desc:`두 분의 일지가 ${BCN[ba]}${BCN[bb]} 충(冲)을 이룹니다. 업무 방식, 속도, 중요하게 생각하는 가치가 다를 수 있습니다. '업무 매뉴얼'과 '회의 프로세스'를 문서화해두면 충돌 에너지를 생산적으로 전환할 수 있습니다.`});
  }
  if(yrRelList.includes('충')){
    risks.push({title:'"세계관의 충돌, 처음부터 솔직하게 대화하라"',desc:`두 분의 년지(태어난 해의 지지)가 ${BCN[ya]}${BCN[yb]} 충을 이룹니다. 인생을 바라보는 기본 철학이나 가치관이 다를 수 있습니다. 초반에 사업의 목표와 비전을 충분히 공유하는 시간을 가지세요.`});
  }
  if(dayRelList.includes('파')||dayRelList.includes('해')){
    risks.push({title:`"${relLabel(dayRelList)}, 동업 계약서가 최고의 부적이다"`,desc:'두 분의 일지 관계에서 서서히 균열이 생길 수 있는 기운이 보입니다. 금전 분배, 의사결정 구조, 퇴사 시 지분 정리 등 껄끄러운 이야기를 사업 초기에 가장 확실하게 문서화해 두세요.'});
  }
  if(risks.length===0){
    risks.push({title:'"관계가 좋아도 계약서는 필수"',desc:'사주 궁합이 좋다고 해서 계약서 없이 시작하는 실수를 범하지 마세요. 좋을 때 작성한 계약서가 수틀렸을 때 최고의 해결책이 됩니다. 지분 구조와 역할을 문서로 명확히 하는 것이 이 동업을 완성하는 마지막 단계입니다.'});
  }
  
  return {
    personA:{pillarStr:pillarA,label:`${a.dayPillar.stem}${a.dayPillar.branch} 일주 (${metaA.ko})`,obj:metaA.obj,role:metaA.role},
    personB:{pillarStr:pillarB,label:`${b.dayPillar.stem}${b.dayPillar.branch} 일주 (${metaB.ko})`,obj:metaB.obj,role:metaB.role},
    score,scoreLabel,sipAB,sipBA,
    dayBranchRels:dayRelList,dayBranchLabel:relLabel(dayRelList),
    yearBranchRels:yrRelList,yearBranchLabel:relLabel(yrRelList),
    overallTitle:`"${overallMeta}"`,overallMeta,overallText,branchBizText,
    roleA,roleB,
    timing2026:{a:timingA,b:timingB,combined},
    risks,radar,
  };
}